import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";

type AuctionStatus = {
	db: {
		id: string;
		state: "open" | "closed" | "resolved" | "cancelled";
		deadlineAt: number;
		bidCount: number;
		clearingPrice: number | null;
		clearingCurrency: string | null;
		winnerAffiliateOrgId: string | null;
	};
	durableObject: unknown;
};

export function AuctionPanel(props: { offerId: string; side: "brand" | "affiliate" }) {
	const qc = useQueryClient();
	const status = useQuery<AuctionStatus | { error: string }>({
		queryKey: ["auction", props.offerId],
		queryFn: async () => {
			try {
				return await api.get<AuctionStatus>(`/auctions/${props.offerId}/status`);
			} catch (err) {
				return { error: (err as Error).message };
			}
		},
		refetchInterval: 15_000,
	});

	const openMut = useMutation({
		mutationFn: () => api.post(`/auctions/${props.offerId}/open`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["auction", props.offerId] }),
	});
	const resolveMut = useMutation({
		mutationFn: () => api.post(`/auctions/${props.offerId}/resolve`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["auction", props.offerId] }),
	});
	const cancelMut = useMutation({
		mutationFn: () => api.post(`/auctions/${props.offerId}/cancel`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["auction", props.offerId] }),
	});

	const noAuction =
		status.data && "error" in status.data && status.data.error.includes("no_auction");

	if (props.side === "brand" && noAuction) {
		return (
			<div className="mt-2 rounded border border-neutral-200 p-3 text-sm">
				<div className="mb-2 font-medium">Vickrey auction</div>
				<button
					type="button"
					onClick={() => openMut.mutate()}
					disabled={openMut.isPending}
					className="rounded bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
				>
					Start sealed-bid auction
				</button>
				{openMut.error && (
					<div className="mt-2 text-xs text-red-700">{(openMut.error as Error).message}</div>
				)}
			</div>
		);
	}

	if (status.isLoading || !status.data) return null;
	if ("error" in status.data) return null;

	const db = status.data.db;
	const expired = db.state === "open" && db.deadlineAt * 1000 < Date.now();

	return (
		<div className="mt-2 rounded border border-neutral-200 p-3 text-sm">
			<div className="mb-1 flex items-center justify-between">
				<div className="font-medium">Auction · {db.state}</div>
				<div className="text-xs text-neutral-500">
					{db.bidCount} bid(s) · deadline {new Date(db.deadlineAt * 1000).toLocaleString()}
				</div>
			</div>
			{db.state === "resolved" && (
				<div className="text-xs text-emerald-700">
					Winner: <span className="font-mono">{db.winnerAffiliateOrgId ?? "—"}</span> @{" "}
					{db.clearingPrice} {db.clearingCurrency}
				</div>
			)}
			{props.side === "brand" && db.state === "open" && (
				<div className="mt-2 flex gap-2">
					<button
						type="button"
						onClick={() => resolveMut.mutate()}
						disabled={!expired || resolveMut.isPending}
						className="rounded bg-emerald-600 px-3 py-1 text-xs text-white hover:bg-emerald-700 disabled:opacity-50"
					>
						{expired ? "Resolve auction" : "Resolve (after deadline)"}
					</button>
					<button
						type="button"
						onClick={() => cancelMut.mutate()}
						disabled={cancelMut.isPending}
						className="rounded bg-neutral-200 px-3 py-1 text-xs hover:bg-neutral-300"
					>
						Cancel
					</button>
				</div>
			)}
			{props.side === "affiliate" && db.state === "open" && (
				<BidForm
					offerId={props.offerId}
					onSubmitted={() => qc.invalidateQueries({ queryKey: ["auction", props.offerId] })}
				/>
			)}
		</div>
	);
}

function BidForm(props: { offerId: string; onSubmitted: () => void }) {
	const [amount, setAmount] = useState("");
	const submitMut = useMutation({
		mutationFn: () =>
			api.post(`/auctions/${props.offerId}/bid`, { amount: Number.parseFloat(amount) }),
		onSuccess: () => {
			setAmount("");
			props.onSubmitted();
		},
	});
	return (
		<div className="mt-2 flex gap-2">
			<input
				type="number"
				step="0.01"
				placeholder="Your bid"
				value={amount}
				onChange={(e) => setAmount(e.target.value)}
				className="w-32 rounded border px-2 py-1 text-xs"
			/>
			<button
				type="button"
				onClick={() => submitMut.mutate()}
				disabled={!amount || submitMut.isPending}
				className="rounded bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
			>
				Submit sealed bid
			</button>
			{submitMut.error && (
				<div className="text-xs text-red-700">{(submitMut.error as Error).message}</div>
			)}
		</div>
	);
}
